/**
 * Pack quantity step before checkout (free) or updateSubscriptionPacks (paid).
 * Focus: seats | volume | both.
 * Monthly paid increases → next invoice; yearly increases → mid-cycle stub charge.
 * Monthly users can switch to yearly (existing checkout credit path) in this step.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  BASE_TEAM_SEATS,
  BASE_TEAM_PRICE_CENTAVOS,
  EXTRA_SEAT_PRICE_CENTAVOS,
  INBOUND_FREE_MONTHLY,
  INBOUND_PACK_PRICE_CENTAVOS,
  INBOUND_PACK_VOLUME,
  MAX_EXTRA_SEATS,
  MAX_SEATS,
  MAX_VOLUME_PACKS,
  SEAT_PACK_INCLUDED_INBOUND,
  monthsUntilYmd,
  packPricing,
  resolveSeatCount,
  yearlyMidCycleUpgradeCharge,
  type PackInterval,
} from '@x9/billing';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { updateSubscriptionPacks, callableErrorMessage, shouldFallbackToCheckout } from '../services/api';

export type PackFocus = 'seats' | 'volume' | 'both';

type Props = {
  open: boolean;
  onClose: () => void;
  focus?: PackFocus;
  hasPaidAccess: boolean;
  /** Current subscription fields (paid). */
  current?: {
    seatCount?: number;
    seatPacks?: number;
    volumePacks?: number;
    billingInterval?: string;
    renewalDueDate?: string;
    nextDueDate?: string;
  } | null;
  /** Free / monthly→yearly / yearly mid-cycle → navigate to checkout. */
  onCheckout: (opts: {
    interval: PackInterval;
    seatCount: number;
    volumePacks: number;
    mode?: 'pack-upgrade';
  }) => void;
  /** Paid → after successful pack update. */
  onUpdated?: () => void;
};

function brl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function Stepper({
  label,
  hint,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          width="auto"
          size="sm"
          disabled={disabled || value <= min}
          onClick={() => onChange(value - 1)}
        >
          −
        </Button>
        <span className="w-8 text-center text-base font-bold tabular-nums text-foreground">
          {value}
        </span>
        <Button
          variant="secondary"
          width="auto"
          size="sm"
          disabled={disabled || value >= max}
          onClick={() => onChange(value + 1)}
        >
          +
        </Button>
      </div>
    </div>
  );
}

export default function PackUpgradeModal({
  open,
  onClose,
  focus = 'both',
  hasPaidAccess,
  current,
  onCheckout,
  onUpdated,
}: Props) {
  const interval: PackInterval =
    String(current?.billingInterval || 'MONTHLY').toUpperCase() === 'YEARLY'
      ? 'YEARLY'
      : 'MONTHLY';

  // In-place pack updates need a live subscription. Leftover entitlement
  // without an Asaas sub must go through checkout (same as a first buy).
  const canManagePacks = hasPaidAccess && !!current;

  const currentSeats = canManagePacks
    ? resolveSeatCount(current)
    : BASE_TEAM_SEATS;
  const currentExtra = Math.max(0, currentSeats - BASE_TEAM_SEATS);
  const currentVolume = Number.isFinite(Number(current?.volumePacks))
    ? Number(current?.volumePacks)
    : 0;

  const [extraSeats, setExtraSeats] = useState(currentExtra);
  const [volumePacks, setVolumePacks] = useState(
    focus === 'volume' && !hasPaidAccess ? 1 : currentVolume,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successHint, setSuccessHint] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setExtraSeats(canManagePacks ? currentExtra : 0);
    setVolumePacks(
      focus === 'volume' && !canManagePacks ? Math.max(1, currentVolume) : currentVolume,
    );
    setError(null);
    setSuccessHint(null);
    setBusy(false);
  }, [open, canManagePacks, currentExtra, currentVolume, focus]);

  const showSeats = focus === 'seats' || focus === 'both';
  const showVolume = focus === 'volume' || focus === 'both';

  const effectiveSeatCount = showSeats
    ? BASE_TEAM_SEATS + extraSeats
    : (canManagePacks ? currentSeats : 0);

  const pricing = useMemo(() => {
    try {
      return packPricing({
        seatCount: effectiveSeatCount,
        volumePacks,
        interval: canManagePacks ? interval : 'MONTHLY',
      });
    } catch {
      return null;
    }
  }, [effectiveSeatCount, volumePacks, interval, canManagePacks]);

  const yearlyStub = useMemo(() => {
    if (!canManagePacks || interval !== 'YEARLY' || !pricing) return null;
    const currentMonthly = packPricing({
      seatCount: currentSeats,
      volumePacks: currentVolume,
      interval: 'MONTHLY',
    }).monthlyCents;
    const renewal = current?.renewalDueDate || current?.nextDueDate || null;
    const openMonths = monthsUntilYmd(renewal);
    if (pricing.monthlyCents <= currentMonthly || openMonths <= 0) return null;
    return yearlyMidCycleUpgradeCharge({
      currentMonthlyCents: currentMonthly,
      targetMonthlyCents: pricing.monthlyCents,
      openMonths,
    });
  }, [canManagePacks, interval, pricing, currentSeats, currentVolume, current]);

  const inboundTotal =
    INBOUND_FREE_MONTHLY
    + (effectiveSeatCount > 0 ? SEAT_PACK_INCLUDED_INBOUND : 0)
    + volumePacks * INBOUND_PACK_VOLUME;

  const dirty = canManagePacks
    && (effectiveSeatCount !== currentSeats || volumePacks !== currentVolume);
  const isIncrease = canManagePacks && pricing != null && (
    effectiveSeatCount > currentSeats || volumePacks > currentVolume
  );

  const title =
    focus === 'seats'
      ? 'Assentos da equipe'
      : focus === 'volume'
        ? 'Volume de contribuições'
        : 'Ajustar plano';

  const submitFree = () => {
    if (!pricing) return;
    onCheckout({
      interval: 'MONTHLY',
      seatCount: pricing.seatCount,
      volumePacks: pricing.volumePacks,
    });
    onClose();
  };

  const submitPaid = async () => {
    if (!pricing || !dirty) {
      onClose();
      return;
    }
    // Yearly capacity increase → explicit Pay-now checkout (saved/new card).
    if (yearlyStub && yearlyStub.chargeNowCents > 0) {
      onCheckout({
        interval: 'YEARLY',
        seatCount: pricing.seatCount,
        volumePacks: pricing.volumePacks,
        mode: 'pack-upgrade',
      });
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    setSuccessHint(null);
    try {
      const res = await updateSubscriptionPacks(pricing.seatCount, pricing.volumePacks);
      if (res.requiresCheckout) {
        onCheckout({
          interval: 'YEARLY',
          seatCount: pricing.seatCount,
          volumePacks: pricing.volumePacks,
          mode: 'pack-upgrade',
        });
        onClose();
        return;
      }
      const bits: string[] = [];
      if (res.midCycleChargeCents && res.midCycleChargeCents > 0) {
        bits.push(
          `Cobrado agora: ${brl(res.midCycleChargeCents)} (${res.midCycleOpenMonths} mês(es) até a renovação).`,
        );
      }
      bits.push(
        `Próxima cobrança: ${brl(res.nextChargeCents)}${res.billingInterval === 'YEARLY' || interval === 'YEARLY' ? '/ano' : '/mês'}.`,
      );
      setSuccessHint(bits.join(' '));
      onUpdated?.();
      onClose();
    } catch (e) {
      if (shouldFallbackToCheckout(e) && pricing) {
        onCheckout({
          interval: interval === 'YEARLY' ? 'YEARLY' : 'MONTHLY',
          seatCount: pricing.seatCount,
          volumePacks: pricing.volumePacks,
        });
        onClose();
        return;
      }
      setError(callableErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const switchToYearly = () => {
    if (!pricing) return;
    onCheckout({
      interval: 'YEARLY',
      seatCount: pricing.seatCount,
      volumePacks: pricing.volumePacks,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" aria-labelledby="pack-modal-title">
        <DialogHeader>
          <DialogTitle id="pack-modal-title">{title}</DialogTitle>
          <DialogDescription>
            {!canManagePacks && `Equipe a partir de ${brl(BASE_TEAM_PRICE_CENTAVOS)}/mês com ${BASE_TEAM_SEATS} assentos.`}
            {canManagePacks && interval === 'MONTHLY' && 'Só aumentos — entram na próxima fatura mensal. Para reduzir, use Cancelamento na Conta.'}
            {canManagePacks && interval === 'YEARLY' && 'Só aumentos — cobram a extensão até a renovação. Para reduzir, use Cancelamento na Conta.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {showSeats && (
            <Stepper
              label="Assentos extras"
              hint={`Base ${BASE_TEAM_SEATS} inclusos · +${brl(EXTRA_SEAT_PRICE_CENTAVOS)}/mês cada · total ${effectiveSeatCount} assentos`}
              value={extraSeats}
              min={canManagePacks ? currentExtra : 0}
              max={MAX_EXTRA_SEATS}
              disabled={busy}
              onChange={setExtraSeats}
            />
          )}
          {showVolume && (
            <Stepper
              label="Pacotes de volume"
              hint={`+${INBOUND_PACK_VOLUME} contribuições/mês · ${brl(INBOUND_PACK_PRICE_CENTAVOS)}/mês`}
              value={volumePacks}
              min={canManagePacks ? currentVolume : 0}
              max={MAX_VOLUME_PACKS}
              disabled={busy}
              onChange={setVolumePacks}
            />
          )}

          {pricing && (
            <div className="space-y-1 text-sm text-foreground">
              {yearlyStub && yearlyStub.chargeNowCents > 0 ? (
                <>
                  <p>
                    Cobrança agora:{' '}
                    <span className="font-bold">{brl(yearlyStub.chargeNowCents)}</span>
                    {' '}({yearlyStub.openMonths} mês(es) até a renovação, com −20%)
                  </p>
                  <p>
                    Renovação anual:{' '}
                    <span className="font-bold">{brl(pricing.chargeCents)}</span>/ano
                    {' · '}
                    {pricing.seatCount > 0 ? `${pricing.seatCount} assentos` : 'sem assentos'}
                    {' · '}
                    {inboundTotal} contribuições/mês
                  </p>
                </>
              ) : (
                <p>
                  {canManagePacks ? 'Próxima cobrança' : 'Total'}:{' '}
                  <span className="font-bold">{brl(pricing.chargeCents)}</span>
                  {canManagePacks && interval === 'YEARLY' ? '/ano' : '/mês'}
                  {' · '}
                  {pricing.seatCount > 0 ? `${pricing.seatCount} assentos` : 'sem assentos'}
                  {' · '}
                  {inboundTotal} contribuições/mês
                </p>
              )}
            </div>
          )}

          {canManagePacks && interval === 'MONTHLY' && dirty && isIncrease && pricing && (
            <div className="rounded-xl border border-border bg-muted p-3 space-y-2">
              <p className="text-sm text-foreground">
                Economize −20%: mude para anual com esta combinação. O tempo já pago do mensal
                é somado ao período anual.
              </p>
              <p className="text-xs text-muted-foreground">
                Anual:{' '}
                {brl(packPricing({
                  seatCount: pricing.seatCount,
                  volumePacks: pricing.volumePacks,
                  interval: 'YEARLY',
                }).chargeCents)}
                /ano
              </p>
              <Button
                variant="secondary"
                width="full"
                size="sm"
                disabled={busy}
                onClick={switchToYearly}
              >
                Trocar para anual com este plano
              </Button>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
          {successHint && <p className="text-sm text-emerald-700">{successHint}</p>}

          <div className="flex flex-col gap-2 sm:flex-row">
            {canManagePacks ? (
              <Button
                width="full"
                disabled={busy || !pricing || effectiveSeatCount > MAX_SEATS}
                onClick={() => void submitPaid()}
              >
                {busy
                  ? 'Salvando…'
                  : dirty
                    ? (yearlyStub && yearlyStub.chargeNowCents > 0
                      ? `Ir ao pagamento · ${brl(yearlyStub.chargeNowCents)}`
                      : 'Confirmar alteração')
                    : 'Manter como está'}
              </Button>
            ) : (
              <Button
                width="full"
                disabled={!pricing || (pricing.seatCount + pricing.volumePacks < 1)}
                onClick={submitFree}
              >
                Continuar para pagamento
              </Button>
            )}
            <Button variant="secondary" width="full" disabled={busy} onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
